import { getAdditionalUserInfo, UserCredential } from "firebase/auth";
import { FirebaseErrorCode } from "../../enums/firebase-error-code.enum";
import { AuthErrorMessage } from "../../enums/auth-error-message.enum";
import { isAxios } from "../errors/errors.util";

/**
 * Tenant policy refusals (tenant_authentication_plan.md §2.2, §2.6, Phase 3):
 * the backend's 403 `code` → the client-side error code the auth pages switch
 * on. Pure helpers, shared by the login-domain precheck and the sign-up pages.
 */
export const POLICY_REFUSAL_CODES: Record<string, FirebaseErrorCode> = {
    domain_not_allowed: FirebaseErrorCode.domainNotAllowed,
    directory_not_allowed: FirebaseErrorCode.directoryNotAllowed,
    method_not_allowed: FirebaseErrorCode.methodNotAllowed,
    password_expired: FirebaseErrorCode.passwordExpired
};

/** The backend's policy code on a 403, or undefined for any other error. */
export const policyRefusalCode = (error: unknown): string | undefined =>
    isAxios(error, 403) ? (error as any)?.response?.data?.code : undefined;

/**
 * The error the auth pages should see for a refused request: the mapped
 * client code (or the raw backend code when unmapped) with the original
 * attached; null when the error is not a policy refusal.
 */
export const toPolicyRefusalError = (
    error: unknown
): { code: FirebaseErrorCode | string; originalError: unknown } | null => {
    const code = policyRefusalCode(error);
    if (!code) return null;
    return { code: POLICY_REFUSAL_CODES[code] ?? code, originalError: error };
};

/**
 * Only a sign-in that has JUST created the Firebase account may delete it
 * again after a refusal; an existing account is left alone (the refusal may
 * be a policy change the admin will revisit).
 */
export const isRefusedFirstSignIn = (response: UserCredential): boolean =>
    getAdditionalUserInfo(response)?.isNewUser === true;

/**
 * What the login page tells the person for a provider (popup) sign-in error.
 * null = nothing to show (the person closed the popup).
 */
export const providerErrorMessage = (code: unknown): AuthErrorMessage | null => {
    switch (code as FirebaseErrorCode) {
        case FirebaseErrorCode.closePopup:
        case FirebaseErrorCode.cancelPopup:
            return null;
        case FirebaseErrorCode.invalidInvite:
            return AuthErrorMessage.invalidInvite;
        case FirebaseErrorCode.invalidEmail:
            return AuthErrorMessage.invalidEmail;
        case FirebaseErrorCode.forbiddenInvite:
            return AuthErrorMessage.forbiddenInvite;
        case FirebaseErrorCode.invalidProvider:
            return AuthErrorMessage.invalidProvider;
        case FirebaseErrorCode.directoryNotAllowed:
            return AuthErrorMessage.directoryNotAllowed;
        case FirebaseErrorCode.domainNotAllowed:
            return AuthErrorMessage.domainNotAllowed;
        case FirebaseErrorCode.methodNotAllowed:
            return AuthErrorMessage.methodNotAllowed;
        case FirebaseErrorCode.passwordExpired:
            return AuthErrorMessage.passwordExpired;
        case FirebaseErrorCode.accessPermissions:
            return AuthErrorMessage.lackPermissions;
        default:
            return AuthErrorMessage.default;
    }
};
